import { resolvePhoneCountry } from "./phoneUtils";

const escapeRegex = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const normalizeCountrySearchValue = (value = "") =>
	String(value || "")
		.toLowerCase()
		.replace(/^\//, "")
		.replace(/^\+/, "")
		.replace(/[^a-z0-9]/g, "");

const normalizeDialCode = (value = "") => String(value || "").replace(/\D/g, "");

export const buildCountryOption = (value) => {
	if (!value) return null;
	if (typeof value === "object" && value.label) {
		return {
			label: String(value.label),
			iso2: String(value.iso2 || value.code || "").toUpperCase(),
			dialCode: normalizeDialCode(value.dialCode || value.phone || ""),
		};
	}

	const label = String(value);
	const phoneCountry = resolvePhoneCountry({ label });
	return {
		label,
		iso2: String(phoneCountry?.iso2 || "").toUpperCase(),
		dialCode: normalizeDialCode(phoneCountry?.dialCode || ""),
	};
};

export const buildCountryOptions = (values = []) =>
	values
		.map(buildCountryOption)
		.filter(Boolean)
		.sort((a, b) => a.label.localeCompare(b.label));

const getOptionRank = (option, rawQuery = "") => {
	const query = normalizeCountrySearchValue(rawQuery);
	if (!query) return 100;

	const label = normalizeCountrySearchValue(option?.label);
	const iso2 = normalizeCountrySearchValue(option?.iso2);
	const dialCode = normalizeDialCode(option?.dialCode);
	const plusDialCode = normalizeCountrySearchValue(`+${dialCode}`);

	if (label === query || iso2 === query || dialCode === query || plusDialCode === query) return 0;
	if (label.startsWith(query)) return 1;
	if (iso2.startsWith(query)) return 2;
	if (dialCode.startsWith(query) || plusDialCode.startsWith(query)) return 3;
	if (label.includes(query)) return 4;
	if (iso2.includes(query)) return 5;
	if (dialCode.includes(query) || plusDialCode.includes(query)) return 6;
	return Number.POSITIVE_INFINITY;
};

export const filterCountryOptions = (options = [], rawQuery = "") => {
	const query = String(rawQuery || "");
	if (!query.trim()) {
		return [...options].sort((a, b) => a.label.localeCompare(b.label));
	}

	return [...options]
		.map((option) => ({ option, rank: getOptionRank(option, query) }))
		.filter((entry) => Number.isFinite(entry.rank))
		.sort((a, b) => {
			if (a.rank !== b.rank) return a.rank - b.rank;
			return a.option.label.localeCompare(b.option.label);
		})
		.map((entry) => entry.option);
};

export const createCountryFilter = () => (options, state) =>
	filterCountryOptions(options, state?.inputValue || "");

export const splitHighlightedText = (text = "", rawQuery = "") => {
	const query = String(rawQuery || "").trim();
	if (!query) return [{ text: String(text), highlighted: false }];

	const normalizedQuery = normalizeCountrySearchValue(query);
	const normalizedText = normalizeCountrySearchValue(text);
	if (!normalizedQuery || !normalizedText.includes(normalizedQuery)) {
		return [{ text: String(text), highlighted: false }];
	}

	const exactRegex = new RegExp(`(${escapeRegex(query)})`, "ig");
	if (exactRegex.test(String(text))) {
		return String(text)
			.split(exactRegex)
			.filter(Boolean)
			.map((part) => ({
				text: part,
				highlighted: part.toLowerCase() === query.toLowerCase(),
			}));
	}

	return [{ text: String(text), highlighted: true }];
};
